import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApi } from '../base-api';
import { removeApiPrefix } from '@app/helpers/utils/urlFormat.utils';

export interface FileUploadResponse {
    success: boolean;
    message?: string;
    data: {
        id: number;
        original_name: string;
        file_name: string;
        file_path: string;
        file_url?: string;
        file_size: number;
        human_file_size: string;
        mime_type: string;
        file_extension: string;
        description?: string;
        category?: string;
        is_public: boolean;
        metadata?: any;
        uploader: {
            id: number;
            name: string;
            email: string;
        };
        created_at: string;
        updated_at: string;
    };
}

export interface BulkFileUploadResponse {
    success: boolean;
    message?: string;
    data: FileUploadResponse['data'][];
}

@Injectable({
    providedIn: 'root',
})
export class FileUploadService extends BaseApi {
    uploadFile(
        file: File,
        isPublic: boolean = false,
        category?: string,
        folder?: string
    ): Observable<FileUploadResponse> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('is_public', isPublic ? 'true' : 'false');

        if (category) {
            formData.append('category', category);
        }

        if (folder) {
            formData.append('folder', folder);
        }

        return this.post<FileUploadResponse>('files', formData);
    }

    uploadFiles(
        files: File[],
        isPublic: boolean = false,
        category?: string,
        folder?: string
    ): Observable<BulkFileUploadResponse> {
        const formData = new FormData();
        files.forEach((file, index) => {
            formData.append(`files[${index}]`, file);
        });

        formData.append('is_public', isPublic ? 'true' : 'false');

        if (category) {
            formData.append('category', category);
        }

        if (folder) {
            formData.append('folder', folder);
        }

        return this.post<BulkFileUploadResponse>('files/bulk', formData);
    }
    deleteFile(id: number): Observable<{ status: string; message?: string }> {
        return this.delete<{ status: string; message?: string }>(`files/${id}`);
    }

    /**
     * Get the download URL for a file by ID (legacy method)
     */
    getFileDownloadUrl(fileId: number): string {
        return `${this.baseUrl}/files/${fileId}/download`;
    }

    /**
     * Get the public download URL for a file by filename (no auth required)
     */
    getPublicFileUrl(filename: string): string {
        return `${this.baseUrl}/files/public/${filename}`;
    }

    /**
     * Get the private download URL for a file by filename (auth required)
     */
    getPrivateFileUrl(filename: string): string {
        return `${this.baseUrl}/files/private/${filename}`;
    }

    downloadFileByUrl(url: string): Observable<Blob> {
        const formattedUrl = removeApiPrefix(url);
        const actualUrl = `${this.baseUrl}/${formattedUrl}`;

        return this.http.get(actualUrl, {
            responseType: 'blob',
            observe: 'body',
        });
    }

    /**
     * Get direct download URL that can be used in anchor tags (no blob conversion needed)
     */
    getDownloadUrl(url: string, customFilename?: string): string {
        const formattedUrl = removeApiPrefix(url);
        let downloadUrl = `${this.baseUrl}/${formattedUrl}`;

        if (customFilename) {
            downloadUrl += `?filename=${encodeURIComponent(customFilename)}`;
        }

        return downloadUrl;
    }

    /**
     * Download a file by ID (for private files that need authentication)
     */
    downloadFile(fileId: number): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/files/${fileId}/download`, {
            responseType: 'blob',
        });
    }

    /**
     * Download a public file by filename (no authentication required)
     */
    downloadPublicFile(filename: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/files/public/${filename}`, {
            responseType: 'blob',
        });
    }

    /**
     * Download a private file by filename (requires authentication)
     */
    downloadPrivateFile(filename: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/files/private/${filename}`, {
            responseType: 'blob',
        });
    }

    /**
     * Get all unique folders
     */
    getFolders(): Observable<{ success: boolean; message: string; data: string[] }> {
        return this.get<{ success: boolean; message: string; data: string[] }>('files/folders');
    }

    /**
     * Get files by folder
     */
    getFilesByFolder(folder: string, page: number = 1): Observable<any> {
        return this.get<any>('files', {
            params: { folder, page: page.toString() },
        });
    }

    /**
     * Move file to folder
     */
    moveFileToFolder(fileId: number, folder: string): Observable<{ success: boolean; message: string }> {
        return this.patch<{ success: boolean; message: string }>(`files/${fileId}/move-to-folder`, { folder });
    }
}
